import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  getBookingRules, updateBookingRules,
  listZones, upsertZone, deleteZone,
} from "@/lib/admin.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const getRules = useServerFn(getBookingRules);
  const saveRules = useServerFn(updateBookingRules);
  const zFn = useServerFn(listZones);
  const zUp = useServerFn(upsertZone);
  const zDel = useServerFn(deleteZone);
  const qc = useQueryClient();

  const { data: rules } = useQuery({ queryKey: ["booking-rules"], queryFn: () => getRules() });
  const { data: zones = [] } = useQuery({ queryKey: ["zones"], queryFn: () => zFn() });

  const [form, setForm] = useState<{
    id: number; auto_confirm_max_party: number; group_min_party: number;
    group_deposit_per_guest_cents: number; pool_deposit_cents: number; late_cutoff_hour: number;
  } | null>(null);
  useEffect(() => { if (rules && !form) setForm(rules as never); }, [rules, form]);

  const save = useMutation({
    mutationFn: async () => { if (form) await saveRules({ data: form }); },
    onSuccess: () => { toast.success("Settings saved"); qc.invalidateQueries({ queryKey: ["booking-rules"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const [zName, setZName] = useState("");
  const [zOrder, setZOrder] = useState(0);

  const addZone = useMutation({
    mutationFn: async () => { await zUp({ data: { name: zName.trim(), display_order: zOrder } }); },
    onSuccess: () => { toast.success("Zone added"); setZName(""); setZOrder(0); qc.invalidateQueries({ queryKey: ["zones"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const rmZone = useMutation({
    mutationFn: (id: string) => zDel({ data: { id } }),
    onSuccess: () => { toast.success("Zone removed"); qc.invalidateQueries({ queryKey: ["zones"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-10">
      <div>
        <h1 className="font-display text-3xl">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">Operational rules and floor zones.</p>
      </div>

      {form && (
        <form
          onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
          className="grid gap-4 rounded-2xl border border-border bg-card/40 p-5 md:grid-cols-2"
        >
          <div className="md:col-span-2 text-sm font-semibold">Booking rules</div>
          <div><Label>Auto-confirm party ≤</Label><Input type="number" value={form.auto_confirm_max_party} readOnly /></div>
          <div><Label>Email-only from (party size)</Label><Input type="number" value={form.group_min_party} readOnly /></div>
          <div><Label>Group deposit / guest (cents)</Label><Input type="number" value={form.group_deposit_per_guest_cents} onChange={(e) => setForm({ ...form, group_deposit_per_guest_cents: +e.target.value })} /></div>
          <div><Label>Pool deposit (cents)</Label><Input type="number" value={form.pool_deposit_cents} onChange={(e) => setForm({ ...form, pool_deposit_cents: +e.target.value })} /></div>
          <div><Label>Late cutoff hour</Label><Input type="number" min={0} max={23} value={form.late_cutoff_hour} onChange={(e) => setForm({ ...form, late_cutoff_hour: +e.target.value })} /></div>
          <div className="md:col-span-2"><Button type="submit" disabled={save.isPending}>Save settings</Button></div>
        </form>
      )}

      <div>
        <h2 className="font-display text-xl">Zones</h2>
        <form
          onSubmit={(e) => { e.preventDefault(); if (zName.trim()) addZone.mutate(); }}
          className="mt-4 grid gap-3 md:grid-cols-[1fr_140px_auto]"
        >
          <Input placeholder="Zone name (e.g. Main Bar, Snug)" value={zName} onChange={(e) => setZName(e.target.value)} />
          <Input type="number" placeholder="Order" value={zOrder} onChange={(e) => setZOrder(+e.target.value)} />
          <Button type="submit" disabled={addZone.isPending}><Plus size={14} className="mr-1" />Add zone</Button>
        </form>
        <div className="mt-4 rounded-2xl border border-border bg-card/30">
          {zones.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No zones yet.</div>
          ) : (
            <ul className="divide-y divide-border">
              {zones.map((z) => (
                <li key={z.id} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <div className="text-sm font-semibold">{z.name}</div>
                    <div className="text-xs text-muted-foreground">Order {z.display_order}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { if (confirm("Delete zone?")) rmZone.mutate(z.id); }}>
                    <Trash2 size={14} className="text-destructive" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
